import { MachineDto, MachineId, ProductDetailsDto, Tenant, TenantDto, tenantFromTenantDto, TenantId, ValidationDto } from '@allbesmart/web-app-common';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { first, map, merge, Observable, of, Subject, Subscription } from "rxjs";
import { environment } from "../../environments/environment";
import { CreateTenantDto } from '../models/tenant/create-tenant-dto';
import { UpdateTenantDto } from '../models/tenant/update-tenant-dto';
import { DeploymentFlagsDto } from '../models/tenant/tenant-flags-dto';
import { HTTP_ENDPOINTS } from "../external/http";
import { OaiboxTelemetryService } from "./oaibox-telemetry.service";
import { FlexRicTelemetryService } from "./flex-ric-telemetry.service";
import { AiRanTelemetryService } from './ai-ran-telemetry.service';
import Keycloak from 'keycloak-js';
import { UserRole } from '@allbesmart/web-app-keycloak-auth';
import { isAiRanType } from "../helpers/oaibox-type";

@Injectable({
  providedIn: 'root'
})
export class TenantManagementService {

  private tenantsBuffer: Tenant[] = [];
  private tenantsBuffer$: Subject<Tenant[]> = new Subject();
  private fetchValidationSubscription?: Subscription;

  private isSalesOnly: boolean;

  constructor(
    keycloak: Keycloak,
    private http: HttpClient,
    private oaiboxTelemetryService: OaiboxTelemetryService,
    private aiRanTelemetryService: AiRanTelemetryService,
    private flexRicTelemetryService: FlexRicTelemetryService
  ) {
    this.isSalesOnly = keycloak.hasRealmRole(UserRole.SALES) &&
                       !keycloak.hasRealmRole(UserRole.ROOT) && !keycloak.hasRealmRole(UserRole.ADMIN);
  }

  public createTenant(newTenantDto: CreateTenantDto): Observable<void> {
    return this.http.post<void>(`${environment.apiBasePath}/${HTTP_ENDPOINTS.TENANTS_BASE_PATH}`, newTenantDto).pipe(first());
  }

  public updateTenant(tenantId: TenantId, updateTenant: UpdateTenantDto): Observable<Tenant> {
    const endpoint = `${environment.apiBasePath}/${HTTP_ENDPOINTS.TENANTS_BASE_PATH}/${tenantId}${this.isSalesOnly ? '/sales' : ''}`;

    return this.http.put<TenantDto>(endpoint, updateTenant)
      .pipe(first(), map(tenantDto => tenantFromTenantDto(tenantDto)));
  }

  public unlinkMachineFromTenant(tenantId: TenantId, machineId: MachineId): Observable<TenantDto | undefined> {
    return this.http.put<TenantDto>(`${environment.apiBasePath}/${HTTP_ENDPOINTS.TENANT_MACHINE_BASE_PATH}/${HTTP_ENDPOINTS.UNLINK_MACHINE}/${tenantId}/${machineId}`, {})
      .pipe(first());
  }

  public updateMachineProductDetails(tenantId: TenantId, machineId: MachineId, productDetailsDto: ProductDetailsDto): Observable<MachineDto> {
    return this.http.put<MachineDto>(`${environment.apiBasePath}/${HTTP_ENDPOINTS.TENANT_MACHINE_BASE_PATH}/${tenantId}/${machineId}`, productDetailsDto)
      .pipe(first())
  }

  public updateMachineSerialNumber(tenantId: TenantId, machineId: MachineId, serialNumber: string): Observable<void> {
    return this.http.put<void>(`${environment.apiBasePath}/${HTTP_ENDPOINTS.TENANT_MACHINE_BASE_PATH}/${HTTP_ENDPOINTS.UPDATE_SERIAL_NUMBER}/${tenantId}/${machineId}`,
      {oaiboxSerialNumber: serialNumber}).pipe(first())
  }

  public deleteMachineFromTenant(tenantId: TenantId, machineId: MachineId): Observable<TenantDto | void> {
    return this.http.delete<TenantDto>(`${environment.apiBasePath}/${HTTP_ENDPOINTS.DELETE_MACHINE}/${tenantId}/${machineId}`)
      .pipe(first());
  }

  public updateClientDomain(tenantId: TenantId, clientDomain: string): Observable<void> {
    return this.http.put<void>(`${environment.apiBasePath}/${HTTP_ENDPOINTS.TENANTS_BASE_PATH}/${tenantId}/${HTTP_ENDPOINTS.UPDATE_CLIENT_DOMAIN}`,
      {clientDomain: clientDomain}).pipe(first());
  }

  public deleteTenant(tenantId: TenantId): Observable<void> {
    return this.http.delete<void>(`${environment.apiBasePath}/${HTTP_ENDPOINTS.TENANTS_BASE_PATH}/${tenantId}`).pipe(first());
  }

  public getTenants(): Tenant[] {
    return this.tenantsBuffer;
  }

  public getTenantByTenantId(tenantId: TenantId): Tenant | undefined {
    return this.tenantsBuffer.find(tenant => tenant.id === tenantId);
  }

  public getTenants$(): Observable<Tenant[]> {
    if (this.tenantsBuffer.length > 0) {
      return merge(of(this.tenantsBuffer), this.tenantsBuffer$);
    } else {
      return this.tenantsBuffer$;
    }
  }

  public refreshTenants(): void {
    this.fetchValidationSubscription?.unsubscribe();
    this.fetchValidationSubscription = this.fetchMyTenants().subscribe(myTenants => {
      myTenants.forEach(tenant => {
        const tenantOptional = this.tenantsBuffer.find(savedTenant => savedTenant.id === tenant.id);
        if (tenantOptional) {
          // Update Tenant
          this.updateTenantFromFreshTenant(tenantOptional, tenant);
        } else {
          // Create Tenant
          this.tenantsBuffer.push(tenant);
        }
        tenant.registeredMachines
          .forEach(machine => this.listenMachineTelemetry(tenant, machine));
      });

      [...this.tenantsBuffer].forEach(bufferedTenant => {
        const tenantOptional = myTenants.find(tenant => bufferedTenant.id === tenant.id);
        //Tenant does not exist anymore
        if (!tenantOptional) this.removeTenant(bufferedTenant);
      });

      this.tenantsBuffer$.next(this.tenantsBuffer);
    });
  }

  private updateTenantFromFreshTenant(tenantToUpdate: Tenant, freshTenant: Tenant): void {
    Object.assign(tenantToUpdate, { ...freshTenant, registeredMachines: tenantToUpdate.registeredMachines });

    [...tenantToUpdate.registeredMachines].forEach(machineToUpdate => {
      const freshMachineOptional = freshTenant.registeredMachines
        .find(machine => machine.id === machineToUpdate.id);
      if (freshMachineOptional) {
        // Update Machine
        machineToUpdate.oaiboxSerialNumber = freshMachineOptional.oaiboxSerialNumber;
        machineToUpdate.productDetails = freshMachineOptional.productDetails;
      } else {
        // Remove Machine
        this.stopListeningMachineTelemetry(machineToUpdate);
        tenantToUpdate.registeredMachines
          .splice(tenantToUpdate.registeredMachines.indexOf(machineToUpdate), 1);
      }
    });

    freshTenant.registeredMachines.forEach(freshMachine => {
      const machineToUpdateOptional = tenantToUpdate.registeredMachines
        .find(machine => machine.id === freshMachine.id);
      if (!machineToUpdateOptional) {
        // Is a new machine
        tenantToUpdate.registeredMachines.push({...freshMachine});
        this.listenMachineTelemetry(freshTenant, freshMachine);
      }
    });
  }

  private listenMachineTelemetry(tenant: Tenant, machine: MachineDto) {
    if (isAiRanType(machine.productDetails.oaiboxType)) {
      this.aiRanTelemetryService.listenToAiRanTelemetry(tenant.id, machine.id);
    } else {
      this.oaiboxTelemetryService.listenToOaiboxTelemetry(tenant.id, machine.id);
    }
    if (tenant.flexRic) this.flexRicTelemetryService.listenToFlexRicTelemetry(tenant.id, machine.id);
  }

  private stopListeningMachineTelemetry(machine: MachineDto) {
    this.oaiboxTelemetryService.stopListeningOaiboxTelemetry(machine.id);
    this.aiRanTelemetryService.stopListeningAiRanTelemetry(machine.id);
    this.flexRicTelemetryService.stopListeningFlexRicTelemetry(machine.id);
  }


  private removeTenant(tenant: Tenant): void {
    tenant.registeredMachines
      .forEach(machine => this.stopListeningMachineTelemetry(machine));
    this.tenantsBuffer.splice(this.tenantsBuffer.indexOf(tenant), 1);
  }

  public fetchTenant(tenantId: string): Observable<Tenant> {
    return this.http.get<TenantDto>(`${environment.apiBasePath}/${HTTP_ENDPOINTS.TENANTS_BASE_PATH}/${tenantId}`)
      .pipe(first(), map(tenantDto => tenantFromTenantDto(tenantDto)));
  }

  public fetchMyTenants(): Observable<Tenant[]> {
    const endpoint = this.isSalesOnly
      ? `${HTTP_ENDPOINTS.TENANT_VALIDATION}/sales`
      : HTTP_ENDPOINTS.TENANT_VALIDATION;

    return this.http.get<ValidationDto>(`${environment.apiBasePath}/${endpoint}`)
      .pipe(first(), map(validationDto => validationDto.availableTenants?.map(tenant => tenantFromTenantDto(tenant)) ?? []));
  }

  public getTenantFlags(tenantId: TenantId): Observable<DeploymentFlagsDto> {
    return this.http.get<DeploymentFlagsDto>(`${environment.apiBasePath}/${HTTP_ENDPOINTS.TENANTS_BASE_PATH}/${tenantId}/${HTTP_ENDPOINTS.TENANT_AUTHORIZATION}`)
      .pipe(first());
  }

  public updateTenantFlags(tenantId: TenantId, TenantFlagsDto: DeploymentFlagsDto): Observable<DeploymentFlagsDto> {
    return this.http.put<DeploymentFlagsDto>(`${environment.apiBasePath}/${HTTP_ENDPOINTS.TENANTS_BASE_PATH}/${tenantId}/${HTTP_ENDPOINTS.TENANT_AUTHORIZATION}`, TenantFlagsDto)
      .pipe(first());
  }

  public createOrUpdateTenantLogo(tenantId: TenantId, file: File): Observable<void> {
    const formData = new FormData();
    formData.append('file', file);

    return this.http.post<void>(`${environment.apiBasePath}/${HTTP_ENDPOINTS.TENANTS_BASE_PATH}/${tenantId}/${HTTP_ENDPOINTS.TENANT_LOGO}`, formData)
      .pipe(first());
  }

  public deleteTenantLogo(tenantId: TenantId): Observable<void> {
    return this.http.delete<void>(`${environment.apiBasePath}/${HTTP_ENDPOINTS.TENANTS_BASE_PATH}/${tenantId}/${HTTP_ENDPOINTS.TENANT_LOGO}`)
      .pipe(first());
  }

  public getLogoUrl(tenantId: TenantId): string {
    return `${environment.apiBasePath}/${HTTP_ENDPOINTS.TENANTS_BASE_PATH}/${tenantId}/${HTTP_ENDPOINTS.TENANT_LOGO}?random=${Math.random()}`
  }

}
